import { FileText, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

/** DB title often defaults to "Untitled Note" while file_path is authoritative — prefer basename for the card headline. */
export function pickDocumentResultDisplayTitle(
  title: string | undefined,
  filePath: string | undefined
): string {
  const t = (title || '').trim()
  const path = (filePath || '').trim()
  const generic = !t || t === 'Untitled Note' || t === 'Document'
  if (generic) {
    if (path) {
      const base = path.split('/').pop() || path
      const withoutMd = base.replace(/\.md$/i, '')
      if (withoutMd) return withoutMd
    }
    return 'Document'
  }
  return t || path || 'Document'
}

interface DocumentResultCardProps {
  title: string
  filePath?: string
  /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend — shown instead of filePath (internal storage path, never rendered). */
  displayPath?: string | null
  /** Full shareable webapp URL (flownotes.space/{workspaceSlug}/{docSlug}), when available. */
  url?: string | null
  documentId?: string
  action: 'created' | 'updated' | 'appended'
  onOpen?: (documentId: string) => void
  className?: string
}

export function DocumentResultCard({
  title,
  filePath,
  displayPath,
  url,
  documentId,
  action,
  onOpen,
  className
}: DocumentResultCardProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    
    const docId = documentId || filePath
    console.log('[DocumentResultCard] Clicked:', { documentId, filePath, title, onOpen: !!onOpen })
    if (onOpen && docId) {
      onOpen(docId)
    } else {
      console.warn('[DocumentResultCard] Cannot open - missing onOpen handler or document ID/path')
    }
  }
  
  const displayTitle = pickDocumentResultDisplayTitle(title, filePath) || documentId || 'Document'

  const actionLabels = {
    created: 'Created',
    updated: 'Modified',
    appended: 'Modified'
  }

  const actionColors = {
    created: 'bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-400',
    updated: 'bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-400',
    appended: 'bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-400'
  }

  return (
    <div
      className={cn(
        "group relative rounded-lg border p-4 transition-all hover:shadow-md cursor-pointer",
        actionColors[action],
        className
      )}
      onClick={handleClick}
      onMouseDown={(e) => {
        // Prevent any default browser behavior that might trigger navigation
        e.preventDefault()
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="mt-0.5 flex-shrink-0">
          <FileText className="h-5 w-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wide opacity-70">
              {actionLabels[action]}
            </span>
            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-70 transition-opacity" />
          </div>
          
          <div className="font-medium text-sm mb-1.5 line-clamp-2">
            {displayTitle}
          </div>
          
          {/* Never render filePath/filename directly — it's an internal storage path (e.g. ws/{orgId}/{slug}.md). */}
          {displayPath && displayPath !== displayTitle && (
            <div className="text-xs text-muted-foreground font-mono break-all line-clamp-1" title={url || undefined}>
              {displayPath}
            </div>
          )}
          
          {!displayPath && !filePath && documentId && (
            <div className="text-xs text-muted-foreground font-mono break-all line-clamp-1">
              ID: {documentId.substring(0, 8)}...
            </div>
          )}
        </div>
      </div>
      
      {/* Hover indicator */}
      <div className="absolute inset-0 rounded-lg border-2 border-transparent group-hover:border-current opacity-0 group-hover:opacity-20 transition-opacity pointer-events-none" />
    </div>
  )
}

